import { useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { ValuationHero } from './components/ValuationHero';
import { UnderstandingPropertyValue } from './components/UnderstandingPropertyValue';
import { IncomeValuationApproach } from './components/IncomeValuationApproach';
import { SalesComparisonApproach } from './components/SalesComparisonApproach';
import { CostApproach } from './components/CostApproach';
import { PricePerUnitMethod } from './components/PricePerUnitMethod';
import { CapRateValuation } from './components/CapRateValuation';
import { PricePerSquareFoot } from './components/PricePerSquareFoot';
import { GrossRentMultiplier } from './components/GrossRentMultiplier';
import { PropertyConditionImpact } from './components/PropertyConditionImpact';
import { BeingHonestAboutCondition } from './components/BeingHonestAboutCondition';
import { FactorsAffectingValue } from './components/FactorsAffectingValue';
import { MarketConditionsImpact } from './components/MarketConditionsImpact';
import { ProfessionalAppraisalProcess } from './components/ProfessionalAppraisalProcess';
import { MaximizingPropertyValue } from './components/MaximizingPropertyValue';
import { CommonValuationMistakes } from './components/CommonValuationMistakes';
import { ValuationFAQ } from './components/ValuationFAQ';
import { ValuationFinalCTA } from './components/ValuationFinalCTA';

export default function ApartmentBuildingValuation() {
  useEffect(() => {
    // Set document title
    document.title = 'How Much Is My Apartment Building Worth? Complete Valuation Guide 2024';
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Discover how much your apartment building is worth using 3 primary valuation methods: income approach, sales comparison, and cost approach. Learn cap rate, price per unit, price per square foot, and gross rent multiplier calculations. Get accurate apartment complex valuations and honest as-is condition assessments.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Discover how much your apartment building is worth using 3 primary valuation methods: income approach, sales comparison, and cost approach. Learn cap rate, price per unit, price per square foot, and gross rent multiplier calculations. Get accurate apartment complex valuations and honest as-is condition assessments.';
      document.head.appendChild(meta);
    }

    // Set meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'apartment building worth, apartment building valuation, income valuation approach, sales comparison approach, cost approach, price per unit, cap rate calculation, price per square foot, gross rent multiplier, apartment complex value, as-is property condition');
    } else {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      metaKeywords.setAttribute('content', 'apartment building worth, apartment building valuation, income valuation approach, sales comparison approach, cost approach, price per unit, cap rate calculation, price per square foot, gross rent multiplier, apartment complex value, as-is property condition');
      document.head.appendChild(metaKeywords);
    }

    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://sellapartmentscash.com/apartment-building-valuation');
    } else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', 'https://sellapartmentscash.com/apartment-building-valuation');
      document.head.appendChild(canonical);
    }

    // Add structured data for Article with FAQ
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "How Much Is My Apartment Building Worth? Complete Valuation Guide 2024",
      "description": "Comprehensive guide to apartment building valuation methods including income approach, sales comparison, cost approach, cap rate, price per unit, and honest condition assessments.",
      "author": {
        "@type": "Organization",
        "name": "Sell Apartments Cash"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Sell Apartments Cash",
        "logo": {
          "@type": "ImageObject",
          "url": "https://sellapartmentscash.com/logo.png"
        }
      },
      "datePublished": "2024-01-15",
      "dateModified": new Date().toISOString().split('T')[0],
      "mainEntity": {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How much is my apartment building worth?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Your apartment building's worth is determined by three primary valuation methods: the income approach (based on NOI and cap rate), sales comparison approach (comparing recent sales of similar properties), and cost approach (replacement cost minus depreciation). Quick estimates can be made using price per unit, cap rate, price per square foot, or gross rent multiplier calculations."
            }
          },
          {
            "@type": "Question",
            "name": "What is the income valuation approach for apartment buildings?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "The income valuation approach calculates property value by dividing the Net Operating Income (NOI) by the market cap rate. For example, if your property generates $100,000 in NOI and the market cap rate is 7%, the value would be approximately $1,428,571. This is the most common method investors use for income-producing properties."
            }
          },
          {
            "@type": "Question",
            "name": "How do I calculate price per unit for my apartment building?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Price per unit is calculated by dividing the total sale price by the number of units. For example, a 20-unit building selling for $2,000,000 has a price per unit of $100,000. This quick metric helps you compare your property to similar apartment buildings in your market and provides a ballpark valuation estimate."
            }
          },
          {
            "@type": "Question",
            "name": "What is a cap rate and how does it affect my apartment building's value?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Cap rate (capitalization rate) is the ratio of Net Operating Income to property value, expressed as a percentage. A lower cap rate means higher property value, while a higher cap rate means lower value. Cap rates typically range from 4-10% depending on location, property condition, and market conditions. To find your property value, divide your NOI by the market cap rate."
            }
          },
          {
            "@type": "Question",
            "name": "Should I be honest about my apartment building's condition when getting it valued?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Absolutely yes. Being honest about your property's as-is condition is crucial for accurate valuation and smooth transactions. Hiding defects can lead to deal cancellations, legal issues, and wasted time. Professional buyers and investors will discover issues during due diligence, so transparency from the start builds trust and often leads to faster closings, even if the property needs significant repairs."
            }
          }
        ]
      }
    };

    // Add structured data script
    let structuredDataScript = document.querySelector('script[type="application/ld+json"]');
    if (structuredDataScript) {
      structuredDataScript.textContent = JSON.stringify(structuredData);
    } else {
      structuredDataScript = document.createElement('script');
      structuredDataScript.type = 'application/ld+json';
      structuredDataScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(structuredDataScript);
    }

    // Cleanup function
    return () => {
      const elementsToRemove = [
        document.querySelector('meta[name="description"]'),
        document.querySelector('meta[name="keywords"]'),
        document.querySelector('link[rel="canonical"]'),
        document.querySelector('script[type="application/ld+json"]')
      ].filter(element => {
        if (!element) return false;
        const content = element.getAttribute('content') || element.getAttribute('href') || element.textContent || '';
        return content.includes('apartment building worth') || content.includes('apartment-building-valuation');
      });
      
      elementsToRemove.forEach(element => element?.remove());
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <ValuationHero />
        <UnderstandingPropertyValue />
        <IncomeValuationApproach />
        <SalesComparisonApproach />
        <CostApproach />
        <PricePerUnitMethod />
        <CapRateValuation />
        <PricePerSquareFoot />
        <GrossRentMultiplier />
        <PropertyConditionImpact />
        <BeingHonestAboutCondition />
        <FactorsAffectingValue />
        <MarketConditionsImpact />
        <ProfessionalAppraisalProcess />
        <MaximizingPropertyValue />
        <CommonValuationMistakes />
        <ValuationFAQ />
        <ValuationFinalCTA />
      </main>
      <Footer />
    </div>
  );
}